import { useEffect, useState } from 'react';
import { CheckSquare, Plus } from 'lucide-react';
import { useTaskStore } from '../../store/taskStore';
import { TaskList, TaskForm } from '../tasks';
import type { Task, PersonDetail } from '../../types';
import { effectiveFirstName, effectiveLastName, effectiveEmail } from '../../lib/personPrecedence';

interface PersonTasksProps {
  person: PersonDetail;
  /**
   * When false, hide edit affordances (Add Task / row-level edit triggers).
   * Defaults to true so other callsites that don't opt in keep current behavior.
   */
  canEdit?: boolean;
}

/**
 * Tasks section for person detail page
 * Shows tasks linked to this person with add/edit capabilities
 */
export const PersonTasks = ({ person, canEdit = true }: PersonTasksProps) => {
  const {
    personTasks,
    personTasksLoading,
    personTasksError,
    fetchPersonTasks,
    clearPersonTasks,
  } = useTaskStore();

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Show completed toggle
  const [showCompleted, setShowCompleted] = useState(false);

  // Fetch tasks on mount
  useEffect(() => {
    fetchPersonTasks(person.id, 1, showCompleted);

    // Cleanup on unmount
    return () => {
      clearPersonTasks();
    };
  }, [person.id, fetchPersonTasks, clearPersonTasks]);

  // Refetch when showCompleted changes
  useEffect(() => {
    fetchPersonTasks(person.id, 1, showCompleted);
  }, [showCompleted, person.id, fetchPersonTasks]);

  // Handle add task
  const handleAddTask = () => {
    setEditingTask(null);
    setIsFormOpen(true);
  };

  // Handle edit task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsFormOpen(true);
  };

  // Handle form close
  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingTask(null);
  };

  // Handle form success
  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingTask(null);
    // Refetch to get updated list
    fetchPersonTasks(person.id, 1, showCompleted);
  };

  // Handle retry
  const handleRetry = () => {
    fetchPersonTasks(person.id, 1, showCompleted);
  };

  // Prepare default person for form (when adding new task). Use effective*
  // so the picker label reflects rep overrides on Blast-sourced people.
  const defaultPerson = {
    id: person.id,
    firstName: effectiveFirstName(person),
    lastName: effectiveLastName(person),
    email: effectiveEmail(person),
    position: person.position,
  };

  // Count pending tasks
  const pendingCount = personTasks.filter((t) => t.status === 'PENDING').length;

  return (
    <div className="card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Tasks
          </h3>
          {pendingCount > 0 && (
            <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Show completed toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blast-600 focus:ring-blast-500 w-3.5 h-3.5"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">Show completed</span>
          </label>

          {/* Add Task button — hidden when the viewer can't edit */}
          {canEdit && (
            <button
              onClick={handleAddTask}
              className="btn-secondary text-sm py-1.5"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Task
            </button>
          )}
        </div>
      </div>

      {/* Task list — only pass onEdit when the viewer can edit, so row-level
          edit affordances are hidden in read-only mode. */}
      <TaskList
        tasks={personTasks}
        isLoading={personTasksLoading}
        error={personTasksError}
        emptyMessage="No tasks for this person"
        showPerson={false} // Don't show person name since we're already on their page
        onEdit={canEdit ? handleEditTask : undefined}
        onRetry={handleRetry}
      />

      {/* Task Form Modal — keep out of the DOM in read-only mode */}
      {canEdit && (
        <TaskForm
          task={editingTask}
          defaultPersonId={person.id}
          defaultPerson={defaultPerson}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};
